import useShowTooltip from 'hooks/useShowTooltip';
import { Chart } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Card, CardHeader, CardBody, Input, Modal, Button, UncontrolledTooltip } from 'reactstrap';
import { removeHtmlTags } from 'helpers/global';
import { textEllipsis } from 'helpers/global';
import { NumericFormat } from 'react-number-format';
import annotation from 'chartjs-plugin-annotation';
import { useCallback, useMemo, useState } from 'react';
import useWhiteLabel from 'hooks/useWhiteLabel';

Chart.plugins.register(annotation);

var colors = {
  gray: {
    100: '#f6f9fc',
    200: '#e9ecef',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#8898aa',
    700: '#525f7f',
    800: '#32325d',
    900: '#212529'
  },
  theme: {
    default: '#172b4d',
    primary: '#5e72e4',
    secondary: '#f4f5f7',
    info: '#11cdef',
    success: '#2dce89',
    danger: '#f5365c',
    warning: '#fb6340',
    brand: '#fe5900'
  },
  black: '#12263F',
  white: '#FFFFFF',
  transparent: 'transparent'
};

const BenchmarkingGraph = ({ isLoading, data: lineGraphData, eyebrow, id }) => {
  const chartSurveyData = useMemo(() => {
    return lineGraphData ? lineGraphData.data.map(({ label, count }) => ({ label, count })) : [];
  }, [lineGraphData]);

  const { isWhiteLabelTeam } = useWhiteLabel();

  const localEyebro = eyebrow || (isWhiteLabelTeam ? 'Index Value Graph' : 'GIV Graph');

  const [sortedData, setSortedData] = useState(chartSurveyData);

  const [selectedSorting, setSelectedSorting] = useState('');

  useShowTooltip();

  const charts = useMemo(() => {
    return {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 16
          }
        },
        elements: {
          point: {
            radius: 6,
            backgroundColor: colors.theme['brand']
          },
          line: {
            tension: 0.4,
            borderWidth: 4,
            borderColor: colors.theme['brand'],
            backgroundColor: colors.transparent,
            borderCapStyle: 'rounded'
          }
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                color: colors.gray[200],
                zeroLineColor: colors.gray[200]
              },
              ticks: {
                stepSize: 0.5,
                beginAtZero: true,
                precision: 0
              }
            }
          ]
        },
        layout: {
          padding: {
            top: 5
          }
        },
        annotation: {
          annotations: [
            {
              type: 'line',
              mode: 'horizontal',
              value: lineGraphData?.avg,
              scaleID: 'y-axis-0',
              borderColor: 'red',
              borderWidth: 2,
              label: {
                content: `Average: ${parseFloat(lineGraphData?.avg).toFixed(2)}`,
                enabled: true,
                position: 'start'
              }
            }
          ]
        }
      },
      data: {
        labels: sortedData.map((elm) => elm.label),
        datasets: [
          {
            label: isWhiteLabelTeam ? 'IV Value' : 'GIV value',
            data: sortedData.map((elm) => elm.count),
            backgroundColor: '#fe5900cc'
          }
        ]
      }
    };
  }, [sortedData, lineGraphData, isWhiteLabelTeam]);

  const sortingData = useCallback(
    (e) => {
      setSelectedSorting(e.target.value);

      if (e.target.value === 'desc') {
        setSortedData(
          [...chartSurveyData].sort(function (a, b) {
            return b.count - a.count;
          })
        );
      } else if (e.target.value === 'asc') {
        setSortedData(
          [...chartSurveyData].sort(function (a, b) {
            return a.count - b.count;
          })
        );
      } else {
        setSortedData(chartSurveyData);
      }
    },
    [chartSurveyData]
  );

  const [isShowModal, setIsShowModal] = useState(false);

  return (
    <>
      <Card className="no-split">
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="surtitle text-black">{localEyebro}</h6>
              <h5 className="h3 mb-0 text-brand result-survey-question-text">
                {textEllipsis(removeHtmlTags(lineGraphData?.name), 40)}
              </h5>
            </div>
            <div>
              {lineGraphData?.avg && (
                <div className="text-sm font-weight-bold text-right mb-2">
                  Average Value:{' '}
                  <NumericFormat value={lineGraphData?.avg} displayType={'text'} decimalScale={2} />
                </div>
              )}
              <div className="d-flex align-items-center d-print-none">
                <span className="mr-2">Sorting:</span>
                <Input type="select" onChange={sortingData} value={selectedSorting}>
                  <option value="">Default</option>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </Input>
                <Button
                  onClick={() => setIsShowModal(true)}
                  className="ml-3 p-1"
                  id={`expand-tooltip-${id}`}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-fullscreen"
                    viewBox="0 0 16 16">
                    <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5M.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5" />
                  </svg>
                </Button>
                <UncontrolledTooltip delay={0} target={`expand-tooltip-${id}`}>
                  Expand Graph
                </UncontrolledTooltip>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody style={{ height: '398px' }}>
          {isLoading && (
            <div className="h-100 d-flex flex-column justify-content-center">
              <p className="text-center mb-0">Loading...</p>
            </div>
          )}
          {!isLoading && (
            <div className="chart">
              <Bar
                data={charts.data}
                options={charts.options}
                id="chart-survey-completed"
                className="chart-canvas"
              />
            </div>
          )}
        </CardBody>
      </Card>
      <Modal
        size="full"
        className="modal-dialog-centered modal-dialog--benchmark-chart"
        isOpen={isShowModal}
        toggle={() => setIsShowModal(false)}>
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            {textEllipsis(removeHtmlTags(lineGraphData?.name), 40)}
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setIsShowModal(false)}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="d-flex align-items-center justify-content-end mb-2">
            <span className="mr-2">Sorting:</span>
            <Input type="select" onChange={sortingData} value={selectedSorting} className="w-auto">
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </Input>
          </div>
          <div className="chart">
            <Bar
              data={charts.data}
              options={charts.options}
              id="chart-survey-completed"
              className="chart-canvas"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BenchmarkingGraph;
